import { useSyncExternalStore } from "react";
import { hasAnyRole, OperationsPrincipals } from "../../auth/roles";
import { useShownRoles } from "../../auth/useShownRoles";
import { useRelease150 } from "../../lib/release150";
import { usePhoneLayoutPreferred } from "../../lib/phone/phoneLayout";

// Release 1.5.0 phone mode: who gets it, and which phone sheet is open (scanner, More, problem report).

/** Phone features exist only with the 1.5.0 switch on and for a person with an Operations role. */
export function usePhoneFeatures(): boolean {
  const release150 = useRelease150();
  const roles = useShownRoles();
  return release150 && hasAnyRole(roles, OperationsPrincipals);
}

/** The phone layout is showing (narrow screen or the person's choice), for a person who may have it. */
export function usePhoneMode(): boolean {
  const features = usePhoneFeatures();
  const preferred = usePhoneLayoutPreferred();
  return features && preferred;
}

/** What a problem is being reported on, when the report is opened from a page. */
export interface ProblemContext {
  workOrderKey?: string | null;
  displayWorkOrder?: string | null;
  operationSequence?: number | null;
  workcenterCode?: string | null;
  machineCode?: string | null;
}

export type PhoneSheet = { kind: "scan" } | { kind: "more" } | { kind: "report"; context: ProblemContext } | null;

let sheet: PhoneSheet = null;
const listeners = new Set<() => void>();

export function openPhoneSheet(next: Exclude<PhoneSheet, null>): void {
  sheet = next;
  listeners.forEach((l) => l());
}

export function closePhoneSheet(): void {
  if (sheet === null) return;
  sheet = null;
  listeners.forEach((l) => l());
}

export function usePhoneSheet(): PhoneSheet {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => sheet,
    () => sheet
  );
}
